import { FC, useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import EnrollmentCard from "../../components/enrollmentCard";
import FooterText from "../../components/footerText";
import NPIInputField from "../../components/inputType";
import EnrollmentPracticeForm from "../../components/enrollmentComp/practiceForm";
import EnrollmentUserForm from "../../components/enrollmentComp/userForm";
import EnrollmentPrescriberForm from "../../components/enrollmentComp/prescriberForm";

import useFetch from "src/hooks/useFetch";
import { ToastContext } from "src/context/toastContext";

import { AddEnrollmentResponseType, EnrollmentPrescriber, EnrollmentUser, NewEnrollmentType, NewEnrollmentTypeForAPI } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import { NPILookupResponse } from "NPILookup";
import { NPISpeciality } from "../addProvider/types";
import { yupResolver } from "@hookform/resolvers/yup";
import { NewEnrollmentSchema } from "src/validation/newEnrollment";
import { LoaderContext } from "src/context/loaderContext";
import { useNavigate } from "react-router-dom";
import { populateEnrollmentForm } from "src/helpers/enrollment";



const NewEnrollment: FC = (): JSX.Element => {
  const [preservedNpi, updatePreservedNpi] = useState<string>("");
  const [showDbSourceModal, setShowDbSourceModal] = useState<boolean>(false);

  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const navigate = useNavigate();
  const { control, formState, handleSubmit, getValues, setValue, watch, reset, trigger, resetField } = useForm<NewEnrollmentType>({
    mode: "all",
    resolver: yupResolver(NewEnrollmentSchema),
    defaultValues: {
      enrollment_contacts_attributes: [
        {} as EnrollmentUser
      ],
      enrollment_doctors_attributes: [
        {} as EnrollmentPrescriber
      ],
    },
  });



  /**
   * Fetching doctor specialities to be rendered in prescriber's seciton
   * @First useFetch API integration
   * @Second useEffect to handle api response
   */
  const { data: specialityData } = useFetch<NPISpeciality[]>(APIEndpoints.GET_PROVIDER_SPECIALITIES);





  /**
   * @NPILookup calling npi lookup for details
   * @first useFetch hook for api call
   * @second useFetch error handler
   * @third useFetch success handler-
   * @fourth npi lookup api trigger
   */
  const { data: npiLookupData, error: npiLookupError, mutate: lookupNpi } = useFetch<NPILookupResponse>(APIEndpoints.NPI_LOOKUP);

  useEffect(() => {
    if (npiLookupData || npiLookupError) {
      hideLoader();
      if(npiLookupData){
        if (npiLookupData.practice_data?.is_from_db) {
          setShowDbSourceModal(true);
        }

        if (npiLookupData.doctor_data !== null || npiLookupData.practice_data !== null) {
          if (npiLookupData.practice_data)
            showToast([`Practice Data Found`], ToastTypes.INFO);
          if (npiLookupData.doctor_data)
            showToast([`Doctor Data Found`], ToastTypes.INFO);

          populateEnrollmentForm(setValue, npiLookupData, getValues("npi") || "");
          trigger(["enrollment_practices_attributes", "enrollment_doctors_attributes"]);
        }

        if (npiLookupData.doctor_data === null && npiLookupData.practice_data === null) {
          reset();
          setValue("npi", preservedNpi);
          setValue("enrollment_practices_attributes.0.npi", preservedNpi);
          setValue("enrollment_doctors_attributes.0.npi", preservedNpi);
          updatePreservedNpi("");
          showToast([`No Data Found`], ToastTypes.ERROR);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiLookupData, npiLookupError]);

  const callNPILookup = () => {
    const npi = getValues("npi");

    if (!npiLookupData || npi !== preservedNpi) {
      if (!npi || (npi && npi.length === 0) || formState.errors.npi)
        showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
      else {
        setLoader();
        updatePreservedNpi(npi);
        const data = {
          'global_doctor[npi]': npi,
        };
        lookupNpi(null, null, data);
      }
    }
  };





  /**
   * @watchingChangesInFormElements Here we are adding new users/providers in the form
   */
  const userListing = watch("enrollment_contacts_attributes");
  const prescriberListing = watch("enrollment_doctors_attributes");

  const addNewUser = () => {
    const users: EnrollmentUser[] = getValues("enrollment_contacts_attributes");
    const newUser = {} as EnrollmentUser;
    users.push(newUser);

    setValue("enrollment_contacts_attributes", users);
  };

  const removeUser = (index: number) => {
    const users: EnrollmentUser[] = getValues("enrollment_contacts_attributes");
    users.splice(index, 1);

    setValue("enrollment_contacts_attributes", users);
  };

  const addNewPrescriber = () => {
    const prescribers: EnrollmentPrescriber[] = getValues("enrollment_doctors_attributes");
    const newPrescriber = {} as EnrollmentPrescriber;
    prescribers.push(newPrescriber);

    setValue("enrollment_doctors_attributes", prescribers);
  };

  const removePrescriber = (index: number) => {
    const prescribers: EnrollmentPrescriber[] = getValues("enrollment_doctors_attributes");
    prescribers.splice(index, 1);

    setValue("enrollment_doctors_attributes", prescribers);
  };




  /**
   * @AddingNewEnrollment Sending entered validated data to API
   * @first adding useFetch to call api
   * @second useFetch error handler
   * @third useFetch success handler
   * @fourth submit form button action
   */
  const {
    loading: enrollmentLoading,
    error: enrollmentError,
    data: enrollmentData,
    mutate: addEnrollment,
    removeErrorAndData,
  } = useFetch<AddEnrollmentResponseType>(APIEndpoints.ADD_ENROLLMENT);

  useEffect(() => {
    if (enrollmentData) {
      const { status: { code, message } } = enrollmentData;
      if (code === 200) {
        showToast([message], ToastTypes.SUCCESS);
        reset();
        navigate("/users/sign_in");
      } else {
        showToast([message], ToastTypes.ERROR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentData]);

  const submitHandler = async (values: NewEnrollmentType) => {
    if (npiLookupData?.practice_data?.is_from_db) {
      setShowDbSourceModal(true);
      return;
    }

    if (enrollmentError || enrollmentData) removeErrorAndData();

    const data: NewEnrollmentTypeForAPI = values as unknown as NewEnrollmentTypeForAPI;
    data.enrollment_practices_attributes[0].program = process.env.REACT_APP_CLIENT_DB || "";
    addEnrollment({ enrollment: data });
  };

  return (
      <div className="enrollment-new d-flex flex-fill flex-column justify-content-between overflow-auto">
        <div
          className={`modal fade ${showDbSourceModal ? "show show-modal" : ""}`}
          tabIndex={-1}
          data-bs-backdrop="true"
          aria-hidden={!showDbSourceModal}
          onClick={() => setShowDbSourceModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "600px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-body text-center">
                <i className="bi bi-exclamation-circle-fill text-warning fs-1"></i>
                <h3>Warning</h3>
                <p className="fs-6">
                  This practice has already been registered in our system. If you would like to add a new provider or user,
                  please log in to the HUB Portal and use the Add Provider or Add User options available under the User Management menu.
                </p>
                <p className="fs-6">
                  If you need further assistance, please reach out to a support team member directly by calling 855-802-8746
                </p>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary btn-lg px-5"
                  onClick={() => setShowDbSourceModal(false)}
                >
                  Ok
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="container container-short mt-3">
            <h1 className="text-primary">My QUTENZA Connect Practice Registration</h1>
            <p>Fill out the form to register your practice in the My QUTENZA Connect Savings Program.</p>
            <p>
              To secure your privacy, this session will time out if left idle for 15 minutes.
              Information will not be saved and you will need to restart registration.
            </p>
            <form onSubmit={handleSubmit(submitHandler)}>

              <EnrollmentCard isTextCenter={false} isCrossBtnVisible={false}
                              cardHeader="NATIONAL PROVIDER IDENTIFIER (NPI)">
                <>
                  <p className="text-start">
                    Auto populate form with NPI. (If NPI does not auto populate, please fill in the remaining fields.)
                  </p>
                  <Controller
                      name="npi"
                      control={control}
                      defaultValue=""
                      render={({field, fieldState: {error}}) => (
                          <NPIInputField
                              inputId="npi"
                              isRequired={false}
                              isRowBased={true}
                              inputPlaceholder="NPI should be 10 digits"
                              field={field}
                              error={error}
                              onBlur={callNPILookup}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  callNPILookup();
                                }
                              }}
                          />
                      )}
                  />
                </>
              </EnrollmentCard>

              <small>All fields marked with * are required</small>
              <EnrollmentCard isTextCenter={false} isCrossBtnVisible={false} cardHeader="Practice">
                <EnrollmentPracticeForm
                    parentNpi={preservedNpi}
                    control={control}
                    errors={formState.errors}
                    getValues={getValues}
                    setValue={setValue}
                    resetField={resetField}
                    trigger={trigger}
                />
              </EnrollmentCard>

              {userListing.map((_d, index) => (
                  <EnrollmentUserForm key={index} removeUser={removeUser} control={control} userIndex={index}/>
              ))}
              <div className="row justify-content-end mt-2 mb-4">
                <div className="col-lg-3 text-end">
                  <button className="btn btn-warning rounded-0" type="button" onClick={addNewUser}>
                    <i className="fas fa-plus"></i> Add User
                  </button>
                </div>
              </div>

              {prescriberListing.map((_d, index) => (
                  <EnrollmentPrescriberForm
                      key={index}
                      parentNpi={preservedNpi}
                      control={control}
                      prescriberIndex={index}
                      errors={formState.errors}
                      getValues={getValues}
                      setValue={setValue}
                      resetField={resetField}
                      trigger={trigger}
                      specialityData={specialityData}
                      removePrescriber={removePrescriber}
                  />
              ))}
              <div className="row justify-content-end mt-2 mb-4">
                <div className="col-lg-3 text-end">
                  <button className="btn btn-warning rounded-0" type="button" onClick={addNewPrescriber}>
                    <i className="fas fa-plus"></i> Add Prescriber
                  </button>
                </div>
              </div>

              <div>
                <p>
                  Once all the required fields are populated, click "Submit Registration." This will prompt the support
                  team to contact your practice to confirm the information provided. To reach a support team member
                  directly, call 855-802-8746.
                </p>
                <p>
                  <small>
                    By clicking on the "Submit Registration" button, you agree that the information you provide will be
                    governed by our site Privacy Policy.
                  </small>
                </p>
              </div>

              <div className="mt-2 mb-4 text-center">
                <button disabled={enrollmentLoading} className="btn btn-warning" type="submit">
                  {enrollmentLoading ? (<i className="spinner-border me-2"></i>) : null}
                  Submit Registration
                </button>
              </div>
            </form>
          </div>
          <FooterText/>
        </div>
      </div>
  );
};

export default NewEnrollment;
